import { memo, useMemo } from 'react';
import {
  BaseEdge,
  getBezierPath,
  EdgeLabelRenderer,
  useReactFlow,
} from 'reactflow';
import useFlowStore from '../../stores/useFlowStore';
import { getSelfConnectingPath } from '../../utils/selfConnectingEdgePath';

/** Closed arrow symbol (aligned with @reactflow/core ArrowClosedSymbol) */
function ArrowClosedGlyph({ color }) {
  return (
    <polyline
      points="-5,-4 0,0 -5,4 -5,-4"
      style={{
        stroke: color,
        fill: color,
        strokeWidth: 1,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    />
  );
}

/**
 * Relationship edge (Crowfoot-style labels).
 * Self-relations (source === target) use an external loop path so the line
 * stays visible above the entity, following the Custom Edges example:
 * https://reactflow.dev/examples/edges/custom-edges
 */
const CrowfootEdge = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style = {},
  selected,
  markerEnd: _propMarkerEnd,
  markerStart: _propMarkerStart,
  interactionWidth,
}) => {
  const { getNode } = useReactFlow();
  const edges = useFlowStore((state) => state.edges);
  const highlightedEdges = useFlowStore((state) => state.highlightedEdges);
  const isHighlighted = highlightedEdges.includes(id);

  const isSelf = source === target;

  const { laneIndex, laneCount } = useMemo(() => {
    if (source !== target) {
      return { laneIndex: 0, laneCount: 1 };
    }
    const selfEdges = edges.filter((e) => e.source === source && e.target === source);
    selfEdges.sort((a, b) => a.id.localeCompare(b.id));
    const idx = selfEdges.findIndex((e) => e.id === id);
    return {
      laneIndex: idx >= 0 ? idx : 0,
      laneCount: Math.max(1, selfEdges.length),
    };
  }, [edges, source, target, id]);

  const { edgePath, labelX, labelY } = useMemo(() => {
    if (isSelf) {
      const { path, labelX: lx, labelY: ly } = getSelfConnectingPath({
        sourceId: source,
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        getNode,
        laneIndex,
        laneCount,
      });
      return { edgePath: path, labelX: lx, labelY: ly };
    }
    const [path, lx, ly] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
    return { edgePath: path, labelX: lx, labelY: ly };
  }, [
    isSelf,
    source,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    getNode,
    laneIndex,
    laneCount,
  ]);

  const { label, cardinality = '1:N' } = data || {};
  const displayLabel = label || cardinality || '';

  const isActive = selected || isHighlighted;
  const edgeColor = isActive ? '#3b82f6' : '#6b7280';
  const strokeWidth = isActive ? 3 : 2;

  const edgeStyle = {
    ...style,
    strokeWidth,
    stroke: edgeColor,
  };

  const markerIdBase = useMemo(
    () => String(id).replace(/[^a-zA-Z0-9_-]/g, '_'),
    [id]
  );

  const { markerEndUrl, markerStartUrl } = useMemo(() => {
    const card = cardinality || '1:N';
    const endId = `crowfoot-${markerIdBase}-end`;
    const startId = `crowfoot-${markerIdBase}-start`;
    let showEnd = false;
    let showStart = false;
    if (card === '1:1') {
      showEnd = false;
      showStart = false;
    } else if (card === '1:N') {
      showEnd = true;
    } else if (card === 'N:1') {
      showStart = true;
    } else if (card === 'N:N') {
      showEnd = true;
      showStart = true;
    }
    return {
      markerEndUrl: showEnd ? `url(#${endId})` : undefined,
      markerStartUrl: showStart ? `url(#${startId})` : undefined,
    };
  }, [cardinality, markerIdBase]);

  return (
    <>
      {(markerEndUrl || markerStartUrl) && (
        <defs>
          {markerEndUrl && (
            <marker
              id={`crowfoot-${markerIdBase}-end`}
              className="react-flow__arrowhead"
              markerWidth="12.5"
              markerHeight="12.5"
              viewBox="-10 -10 20 20"
              markerUnits="strokeWidth"
              orient="auto"
              refX="0"
              refY="0"
            >
              <ArrowClosedGlyph color={edgeColor} />
            </marker>
          )}
          {markerStartUrl && (
            <marker
              id={`crowfoot-${markerIdBase}-start`}
              className="react-flow__arrowhead"
              markerWidth="12.5"
              markerHeight="12.5"
              viewBox="-10 -10 20 20"
              markerUnits="strokeWidth"
              orient="auto-start-reverse"
              refX="0"
              refY="0"
            >
              <ArrowClosedGlyph color={edgeColor} />
            </marker>
          )}
        </defs>
      )}
      <BaseEdge
        id={id}
        path={edgePath}
        style={edgeStyle}
        markerEnd={markerEndUrl}
        markerStart={markerStartUrl}
        interactionWidth={interactionWidth}
      />

      {displayLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className={`nodrag nopan text-xs px-2 py-1 rounded shadow-sm border transition-all ${
              isActive
                ? 'bg-blue-100 dark:bg-blue-900 border-blue-500 text-blue-700 dark:text-blue-300 font-semibold'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
          >
            {displayLabel}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default memo(CrowfootEdge);
